import type { Edge, Node } from 'reactflow';

export interface ConnectedContentItem {
  id: string;
  type: 'link' | 'text';
  value: string;
  label?: string;
}

const HTTP_PREFIX = 'http://';
const HTTPS_PREFIX = 'https://';

const detectContentType = (text: string): 'link' | 'text' =>
  text.startsWith(HTTP_PREFIX) || text.startsWith(HTTPS_PREFIX)
    ? 'link'
    : 'text';

export const filterContentEdges = (
  edges: Edge[] | undefined,
  targetId: string,
): Edge[] =>
  edges?.filter(
    edge => edge?.target === targetId && edge?.targetHandle === 'content',
  ) ?? [];

export const readPersistedContent = (raw: unknown): ConnectedContentItem[] =>
  Array.isArray(raw) ? (raw as ConnectedContentItem[]) : [];

export const buildConnectedContent = (
  contentEdges: Edge[],
  allNodes: Node[],
  persisted: ConnectedContentItem[],
): ConnectedContentItem[] => {
  const persistedById = new Map(persisted.map(item => [item.id, item]));
  const validSourceIds = new Set(contentEdges.map(edge => edge.source));
  const result = new Map<string, ConnectedContentItem>();
  contentEdges.forEach(edge => {
    const sourceNode = allNodes.find(n => n.id === edge.source);
    const liveText = (sourceNode?.data?.text || '').trim();
    const fallback = persistedById.get(edge.source);
    const text = liveText || fallback?.value || '';
    if (!text) return;
    result.set(edge.source, {
      id: edge.source,
      type: detectContentType(text),
      value: text,
      label: fallback?.label,
    });
  });
  persisted.forEach(item => {
    if (validSourceIds.has(item.id) && !result.has(item.id)) {
      result.set(item.id, item);
    }
  });
  return Array.from(result.values());
};

export const isSameContentList = (
  a: ConnectedContentItem[],
  b: ConnectedContentItem[],
): boolean =>
  a.length === b.length &&
  a.every(
    (item, index) =>
      !!b[index] &&
      b[index].id === item.id &&
      b[index].value === item.value &&
      b[index].type === item.type,
  );
